import React, { useState, useEffect, memo } from "react";
import Typewriter from "./utils/Typewriter";
import "./Home.css";
import { Canvas } from "@react-three/fiber";
import Bird from "./canvas/Bird";
import { PrimaryCTA, SecondaryCTA, CTAContainer } from "./components/ui";
import { 
  FaReact, 
  FaJs, 
  FaNodeJs, 
  FaLaravel, 
  FaHtml5, 
  FaCss3Alt, 
  FaGitAlt, 
  FaDocker, 
  FaJava,
  SiTailwindcss, 
  SiSpring, 
  SiMongodb, 
  SiMysql, 
  SiTypescript, 
  SiPostgresql 
} from "./icons";

function Home() {
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Skills in exact order as requested: JavaScript, React, Laravel, Node.js, HTML, CSS, Tailwind, Spring, Git, Docker, MySQL, MongoDB, Java, TypeScript, PostgreSQL
  const skills = [
    { icon: FaJs, color: "#F7DF1E" },           // JavaScript
    { icon: FaReact, color: "#61DAFB" },        // React
    { icon: FaLaravel, color: "#FF2D20" },      // Laravel
    { icon: FaNodeJs, color: "#339933" },       // Node.js
    { icon: FaHtml5, color: "#E34F26" },        // HTML
    { icon: FaCss3Alt, color: "#1572B6" },      // CSS
    { icon: SiTailwindcss, color: "#06B6D4" },  // Tailwind
    { icon: SiSpring, color: "#6DB33F" },       // Spring
    { icon: FaGitAlt, color: "#F05032" },       // Git
    { icon: FaDocker, color: "#2496ED" },       // Docker
    { icon: SiMysql, color: "#336791" },        // MySQL
    { icon: SiMongodb, color: "#47A248" },      // MongoDB
    { icon: FaJava, color: "#007396" },         // Java
    { icon: SiTypescript, color: "#3178C6" },   // TypeScript
    { icon: SiPostgresql, color: "#336791" }    // PostgreSQL
  ];

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Ensure horizontal scroll starts from left on mobile
  useEffect(() => {
    if (isMobile) {
      const scrollContainer = document.querySelector('.hero__skills-container');
      if (scrollContainer) {
        // Reset scroll position to start from left
        scrollContainer.scrollLeft = 0;
      }
    }
  }, [isMobile]);

  // Hide scroll indicator when scrolling
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollIndicator(window.scrollY < 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="hero" id="Home" role="banner" aria-label="Hero section">
      {/* Animated Background Elements */}
      <div className="hero__background" aria-hidden="true">
        <div className="canvas-wrapper">
          <Canvas style={{ position: 'absolute', top: 0, left: 0, zIndex: -1, width: '100%', height: '100%' }}>
            <Bird />
          </Canvas>
        </div>
        <div className="hero__particles" aria-hidden="true"></div>
      </div>

      {/* Main Hero Content */}
      <div className="hero__container">
        <div className="hero__content">
          {/* Left Side - Text and CTAs */}
          <div className="hero__left">
            {/* Welcome Text Section */}
            <div className="hero__intro">
              <h1 className="hero__welcome">WELCOME TO MY UNIVERSE</h1>
              <h2 className="hero__name">Hi, I'm Ilias Lyamoudi</h2>

              {/* Typewriter Effect */}
              <div className="hero__typewriter">
                <Typewriter
                  texts={[
                    'Software Engineer,',
                    'Web Developer,',
                    'Tech Enthusiast,',
                    'React Enthusiast,',
                    'Java Enthusiast,',
                    'Solo Developer,',
                  ]}
                  typingSpeed={150}
                  erasingSpeed={50}
                  eraseDelay={1000}
                />
              </div>

              <h4 className="hero__location">Based in Morocco</h4>
            </div>

            {/* CTA Buttons Section */}
            <CTAContainer className="hero__cta">
              <PrimaryCTA 
                href="#Portfolio" 
                ariaLabel="View my portfolio work"
                icon="→"
              >
                View My Work
              </PrimaryCTA>
              <SecondaryCTA 
                href="#Contact" 
                ariaLabel="Get in touch"
                icon="✉"
              >
                Get In Touch
              </SecondaryCTA>
            </CTAContainer>
          </div>

          {/* Right Side - Skills */}
          <div className="hero__right">
            {isMobile ? (
              // Mobile: No animations to prevent scroll interference
              <div className="hero__skills">
                <h3 className="hero__skills-title">Tech Stack</h3>
                <div className="skills__scroll-hint" aria-hidden="true"></div>
                <div className="hero__skills-container" role="list" aria-label="Technology skills">
                  {skills.map((skill, index) => {
                    const IconComponent = skill.icon;
                    return (
                      <div
                        key={index}
                        className="hero__skill-card"
                        role="listitem"
                      >
                        <div className="hero__skill-icon" style={{ color: skill.color }} aria-hidden="true">
                          <IconComponent />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              // Desktop: Full animations
              <div className="hero__skills">
                <h3 className="hero__skills-title">Tech Stack</h3>
                <div className="hero__skills-container" role="list" aria-label="Technology skills">
                  {skills.map((skill, index) => {
                    const IconComponent = skill.icon;
                    return (
                      <div
                        key={index}
                        className="hero__skill-card"
                        role="listitem"
                      >
                        <div className="hero__skill-icon" style={{ color: skill.color }} aria-hidden="true">
                          <IconComponent />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <div className="hero__scroll">
            <div className="scroll__indicator" role="button" tabIndex="0" aria-label="Scroll down to explore more content">
              <span>Scroll to explore</span>
              <div className="scroll__arrow" aria-hidden="true">↓</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default memo(Home);
